import { type FC, type PropsWithChildren, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { Formik } from 'formik'
import { AnimatePresence, type HTMLMotionProps, motion } from 'motion/react'
import { Typography, Box, type BoxProps, Stack } from '@mui/material'

import { TextFieldInput } from '@dhl/inputs'
import { SearchBox, StickySearchBoxContainer } from '@dhl/search'
import { type Servicepoint, ServicepointDetails, ServicepointList } from '@dhl/receiver-servicepoint'

import { Button, Modal, useIsMobileVariant } from '@dhl-ui/components'
import { type ServicepointInputValue } from './ServicepointInputValue.js'
import { ServicepointInputKindInput, type InputKind } from './ServicepointInputKindInput.js'

type FormValues = {
  inputKind: InputKind | null
  postNumber: string
  servicepoint: Servicepoint | null
}

type Props = {
  countryCode: string
  open: boolean
  searchTerm?: string
  servicepoints: Servicepoint[]
  showDetailsFor?: string
  showOpeningTimes?: boolean
  title: string
  onChange: (inputValue: ServicepointInputValue) => void
  onClose?: () => void
  onClosing?: () => void
  onOpen?: () => void
  onSearchTermChange?: (searchTerm: string) => void
  onSearchTermClear?: () => void
  onShowServicePointDetails?: (servicepoint?: Servicepoint) => void
  buttonText?: string
}

type SlideVariants = 'hidden' | 'visible'

type SlideProps = HTMLMotionProps<'div'> & {
  disableTransition?: boolean
  height?: string
  initial?: boolean | SlideVariants
  innerProps?: BoxProps
  visible?: boolean
  variants: Record<SlideVariants, { x: string | number }>
}

const Slide = ({
  children,
  disableTransition,
  initial,
  innerProps,
  variants,
  visible = true,
  ...rest
}: PropsWithChildren<SlideProps>) => (
  <motion.div
    {...rest}
    animate={visible ? 'visible' : 'hidden'}
    initial={initial}
    style={{ minWidth: '100%', overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}
    transition={{
      x: { type: 'tween', duration: disableTransition ? 0 : 0.3, delay: 0 },
      opacity: { duration: disableTransition ? 0 : 0.3 },
    }}
    variants={{
      visible: { x: variants.visible.x, opacity: 1 },
      hidden: { x: variants.hidden.x, opacity: 0 },
    }}
  >
    <Box {...innerProps}>{children}</Box>
  </motion.div>
)

export const ServicepointDialog: FC<Props> = ({
  open,
  countryCode,
  searchTerm,
  servicepoints,
  showDetailsFor,
  showOpeningTimes = true,
  title,
  onClose,
  onClosing,
  onChange,
  onOpen,
  onSearchTermChange,
  onSearchTermClear,
  onShowServicePointDetails,
  buttonText,
}) => {
  const { t } = useTranslation()
  const [selectedServicePoint, setSelectedServicePoint] = useState<Servicepoint | undefined>(
    servicepoints.find((s) => s.servicepointKey === showDetailsFor)
  )
  const [isShowingDetails, setShowDetails] = useState(false)
  const isMobileVariant = useIsMobileVariant()

  const hasPackStationsWithPostNumbers = ['DE', 'LU'].includes(countryCode)

  const filterByInputKind = (inputKind: InputKind): ((servicepoint: Servicepoint) => boolean) => {
    if (hasPackStationsWithPostNumbers) {
      switch (inputKind) {
        case 'PackStation':
          return ({ servicepointType }) => servicepointType === 'PackStation'
        case 'Servicepoint':
          return ({ servicepointType }) => servicepointType === 'Servicepoint'
        default:
          return () => false
      }
    } else {
      return () => true
    }
  }

  const initialValues: FormValues = {
    inputKind: hasPackStationsWithPostNumbers ? null : 'Servicepoint',
    postNumber: '',
    servicepoint: null,
  }

  const onSubmit = ({ postNumber, inputKind, servicepoint }: FormValues) => {
    onClosing?.()

    switch (inputKind) {
      case 'PackStation':
        return onChange({
          inputKind,
          servicepoint: servicepoint!,
          postNumber,
        })
      case 'Servicepoint':
        return onChange({
          inputKind,
          servicepoint: servicepoint!,
        })
      default:
        return Promise.resolve()
    }
  }

  const shouldSkipTransition = useRef(false)

  const handleServicePointClick = (servicepoint: Servicepoint) => {
    shouldSkipTransition.current = false
    setSelectedServicePoint(servicepoint)
    onShowServicePointDetails?.(servicepoint)
    setShowDetails(true)
  }

  const handleBackButtonClick = () => {
    if (isShowingDetails) {
      shouldSkipTransition.current = false
      setShowDetails(false)
      onShowServicePointDetails?.(undefined)
    } else {
      onClose?.()
    }
  }

  const handleClose = () => {
    setSelectedServicePoint(undefined)
    setShowDetails(false)
    onClose?.()
  }

  useEffect(() => {
    if (!showDetailsFor) {
      shouldSkipTransition.current = true
      setShowDetails(false)
    }
  }, [showDetailsFor])

  useLayoutEffect(() => {
    if (open) {
      onOpen?.()
    }
  }, [onOpen, open])

  const validationSchema: Yup.Schema<Partial<Pick<FormValues, 'postNumber'>>> = Yup.object({
    postNumber: Yup.string().when('inputKind', {
      is: 'PackStation',
      then: (schema) => schema.required(t('components.servicePointInput.dialog.postNumberIsRequiredErrorMessage')),
    }),
  })
  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
      {({ values, submitForm, setFieldValue }) => (
        <Modal
          disableBackdropTransition
          isFullscreen={isMobileVariant}
          isSwipeable={isMobileVariant}
          open={open}
          swipeCloseMode="handle"
          onClose={handleClose}
        >
          <Modal.Header
            title={title}
            onBack={isShowingDetails ? handleBackButtonClick : undefined}
            onClose={handleClose}
          />
          <Modal.Content noPaddingSides>
            <Stack flexDirection="row" sx={{ flex: 1, overflow: 'hidden' }}>
              <AnimatePresence initial={false}>
                <Slide
                  disableTransition={shouldSkipTransition.current}
                  visible={!isShowingDetails}
                  variants={{
                    hidden: { x: '-120%' },
                    visible: { x: '0%' },
                  }}
                  innerProps={{ sx: { flex: 1, overflowY: 'auto' } }}
                >
                  {onSearchTermChange && (
                    <StickySearchBoxContainer>
                      <SearchBox
                        searchTerm={searchTerm}
                        sx={{ mb: 2 }}
                        onSearchTermChange={onSearchTermChange}
                        onSearchTermClear={onSearchTermClear}
                      />
                    </StickySearchBoxContainer>
                  )}
                  <Stack spacing={2} mt={-1}>
                    {hasPackStationsWithPostNumbers && (
                      <>
                        <Stack>
                          <Typography variant="h4" gutterBottom>
                            {t('components.servicePointInput.dialog.postNumberTitleText')}
                          </Typography>
                          <Typography gutterBottom>
                            {t('components.servicePointInput.dialog.postNumberDescriptionText')}
                          </Typography>
                        </Stack>

                        <ServicepointInputKindInput name="inputKind" />

                        {values.inputKind === 'PackStation' && (
                          <TextFieldInput
                            name="postNumber"
                            type="number"
                            label={t('components.servicePointInput.dialog.postNumberLabel')}
                            fullWidth
                          />
                        )}
                      </>
                    )}

                    {values.inputKind && (
                      <ServicepointList
                        isDesktop={false}
                        servicepoints={servicepoints.filter(filterByInputKind(values.inputKind))}
                        onClick={handleServicePointClick}
                        showOpeningTimes={showOpeningTimes}
                      />
                    )}
                  </Stack>
                </Slide>

                <Slide
                  disableTransition={shouldSkipTransition.current}
                  initial="hidden"
                  visible={isShowingDetails}
                  variants={{
                    hidden: { x: '20%' },
                    visible: { x: '-100%' },
                  }}
                  innerProps={{ sx: { flex: 1, overflowY: 'auto' } }}
                >
                  {selectedServicePoint && (
                    <ServicepointDetails
                      servicepoint={selectedServicePoint}
                      sx={{ mt: -2 }}
                      selectButtonText={buttonText}
                    />
                  )}
                </Slide>
              </AnimatePresence>
            </Stack>
          </Modal.Content>

          {selectedServicePoint && isShowingDetails && (
            <Modal.Footer>
              <Button
                fullWidth
                color="primary"
                data-testid="select-servicepoint-button"
                variant="contained"
                onClick={() => {
                  setFieldValue('servicepoint', selectedServicePoint)
                  submitForm()
                }}
              >
                {buttonText ??
                  t(
                    `servicepoint.servicepointDetailsDialog.selectButton.${selectedServicePoint.servicepointType.toLowerCase()}`
                  )}
              </Button>
            </Modal.Footer>
          )}
        </Modal>
      )}
    </Formik>
  )
}
